import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { Icon } from '../Icon/Icon';
import { IconName } from '../Icon/Icon.type';
import { Text } from '../Text/Text';
import { Avatar } from '../Avatar/Avatar';
import { Button } from '../Button/Button';
import type { IChatMessageProps } from './ChatMessage.type';
import { TokenUsageTooltip } from '../TokenUsageTooltip';
import type { ITokenUsage, IDevelopmentTiming } from '../TokenUsageInfoModal/TokenUsageInfoModal.type';
import { ArtifactCard } from '../ArtifactCard/ArtifactCard';
import { parseArtifacts } from '../../utils/parseArtifacts';
import { LoadingDots } from '../LoadingDots/LoadingDots';
import { FileTypeIcon } from '@ui-library/DocumentsPanel';
import { Attachment } from '../../types/chat';
import { Permissions } from '../../constants';

// Format duration in seconds to match the Statistics tooltip format
const formatRoundedDuration = (seconds: number): string => {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return `${minutes}m ${remainingSeconds}s`;
};

const FileAttachmentsToolTip = React.memo(({ attachments }: { attachments: Attachment[] }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (isExpanded && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPopupPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX
      });
    }
  }, [isExpanded]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    };

    const handleScroll = () => {
      setIsExpanded(false);
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isExpanded]);

  return (
    <div className="relative">
      <div
        ref={buttonRef}
        onClick={() => setIsExpanded(!isExpanded)}
        className='inline-flex items-center gap-bg-v2-xs border border-border-secondary bg-bg-primary p-bg-v2-sm rounded-bg-v2-xl cursor-pointer hover:bg-bg-secondary transition-colors'
      >
        <Icon name={IconName.Attachment} size='xs' variant="secondary" />
        <Text variant="text-xs" weight="medium" color="secondary" as="span">
          {`${attachments.length} Attachment${attachments.length > 1 ? 's' : ''}`}
        </Text>
        <div className={`pr-bg-v2-sm transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
          <Icon name={IconName.ChevronDown} size='xs' variant="secondary" />
        </div>
      </div>

      {isExpanded && createPortal(
        <div
          className="fixed min-w-[250px] max-w-[400px] bg-bg-primary border border-border-secondary rounded-bg-v2-md container-shadow-lg"
          style={{
            top: `${popupPosition.top}px`,
            left: `${popupPosition.left}px`
          }}
        >
          <div className="flex flex-col gap-bg-v2-xs p-bg-v2-sm max-h-[300px] overflow-y-auto">
            {attachments.map((attachment, index) => (
              <div key={index} className="flex items-center gap-bg-v2-xs p-bg-v2-sm hover:bg-bg-secondary rounded-bg-v2-sm transition-colors">
                <FileTypeIcon extension={attachment.name.split('.').pop() || ''} size={16} />
                <Text variant="text-xs" weight="regular" color="primary" className="truncate flex-1">
                  {attachment.name}
                </Text>
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
});

export const ChatMessage: React.FC<IChatMessageProps> = ({
  message,
  currentUser,
  className = '',
  // Elaboration props
  isElaboration = false,
  sessionId,
  threadId,
  onApprove,
  onRefine,
  onCancel,
  isProcessing = false,
  showActionButtons = false,
  // Artifact props
  project,
  onArtifactClick,
  isArtifactDetailOpen = false,
  isFullWidthMessages = false,
  // Design mode
  isDesignMode = false,
  // File click handler
  onFileClick,
}) => {
  // Destructure message properties
  const {
    id: messageId,
    role,
    content,
    timestamp: messageTimestamp,
    isThinking = false,
    thinkingMessage = 'Thinking...',
    isStreaming = false,
    toolExecutions = [],
    tokenUsage,
    developmentTiming,
    brainstormTiming,
    attachments = [],
    iterationCount = 1,
    isUpdated = false,
    updatedAt,
  } = message;

  const type = role === 'user' ? 'user' : 'assistant';
  // If message belongs to current user, always use current user data (to reflect profile updates)
  // Otherwise, use stored user_info or fallback to current user
  const isCurrentUserMessage = message.user_info?.id === currentUser?.id;
  const user = isCurrentUserMessage ? {
    id: currentUser?.id || '',
    email: currentUser?.email || '',
    username: currentUser?.username || '',
    firstName: currentUser?.firstName || '',
    lastName: currentUser?.lastName || '',
  } : (message.user_info || {
    id: currentUser?.id || '',
    email: currentUser?.email || '',
    username: currentUser?.username || '',
    firstName: currentUser?.firstName || '',
    lastName: currentUser?.lastName || '',
  });

  // Check if user has update permission (viewers don't have this permission)
  const hasUpdatePermission = project?.permissions?.includes(Permissions.UpdateProjectPermission) ?? true;

  // Format timestamp if it exists
  const timestamp = messageTimestamp ?
    (typeof messageTimestamp === 'string' ? messageTimestamp : messageTimestamp.toLocaleString())
    : undefined;
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [showRefinementInput, setShowRefinementInput] = useState(false);
  const [refinementText, setRefinementText] = useState('');
  const [isRefining, setIsRefining] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  // Animation states for elaboration updates
  const [isAnimating, setIsAnimating] = useState(false);
  const [showNewContent, setShowNewContent] = useState(true);
  const previousContent = useRef(content);
  const contentRef = useRef<HTMLDivElement>(null);
  const statsButtonRef = useRef<HTMLButtonElement>(null);

  // Keep track of the last tool_start message to display in collapsed mode
  // This persists even after the tool completes, until thinking finishes or a new tool starts
  const [lastToolStartMessage, setLastToolStartMessage] = useState<any>(null);

  // Update lastToolStartMessage when a new running tool appears, or clear when thinking stops
  useEffect(() => {
    if (!isThinking) {
      // Clear when thinking finishes
      setLastToolStartMessage(null);
    } else {
      // Get the latest running tool
      const runningTools = toolExecutions
        .filter(tool => tool && tool.tool_name && tool.status === 'running')
        .sort((a, b) => (b.timestamp_start || 0) - (a.timestamp_start || 0));

      if (runningTools.length > 0) {
        // New tool started - update with its "start" message
        setLastToolStartMessage(runningTools[0]);
      }
      // Note: We don't clear when runningTools is empty - we keep showing the last one
    }
  }, [isThinking, toolExecutions]);

  // Handle content updates with animation for elaboration
  useEffect(() => {
    if (isElaboration && previousContent.current !== content && previousContent.current !== '') {
      setIsAnimating(true);
      setShowNewContent(false);

      const timer = setTimeout(() => {
        setShowNewContent(true);
        previousContent.current = content;
        setTimeout(() => setIsAnimating(false), 600);
      }, 200);

      return () => clearTimeout(timer);
    } else {
      previousContent.current = content;
    }
  }, [content, isElaboration]);

  // Elaboration handlers
  const handleRefineClick = () => {
    setShowRefinementInput(true);
    setRefinementText('');
  };

  const handleRefinementSubmit = async () => {
    if (!refinementText.trim() || !onRefine || !sessionId || !threadId) return;

    try {
      setIsRefining(true);
      await onRefine(sessionId, refinementText, threadId);
      setShowRefinementInput(false);
      setRefinementText('');
    } catch (error) {
      console.error('Error submitting refinement:', error);
    } finally {
      setIsRefining(false);
    }
  };

  const handleApprove = async () => {
    if (!onApprove || !sessionId || !threadId) return;

    try {
      setIsApproving(true);
      await onApprove(sessionId, threadId);
    } catch (error) {
      console.error('Error approving elaboration:', error);
    } finally {
      setIsApproving(false);
    }
  };

  const handleCancel = async () => {
    if (!onCancel || !sessionId || !threadId) return;

    try {
      setIsCancelling(true);
      await onCancel(sessionId, threadId);
    } catch (error) {
      console.error('Error cancelling elaboration:', error);
    } finally {
      setIsCancelling(false);
    }
  };

  const cancelRefinement = () => {
    setShowRefinementInput(false);
    setRefinementText('');
  };

  // Filter out invalid tool executions (those without tool_name)
  const validToolExecutions = toolExecutions.filter(tool => tool && tool.tool_name);

  // Separate tools with results (completed/failed) from those currently running
  const toolsWithResults = validToolExecutions.filter(tool => tool.status === 'completed' || tool.status === 'failed');
  const runningTools = validToolExecutions.filter(tool => tool.status === 'running');

  // Reverse tool results array to show latest first (newest at top)
  // The backend sends tools in chronological order (oldest first), so we reverse it
  const sortedToolResults = [...toolsWithResults].reverse();

  // Computed state variables for better readability
  const hasToolResults = toolsWithResults.length > 0;
  // Show timing for both dev mode and brainstorm mode
  const shouldShowThoughtDuration = ((!!developmentTiming?.duration_seconds || !!brainstormTiming?.duration_seconds) && !isThinking);

  // Debug logging for tool executions
  if (isThinking && validToolExecutions.length > 0) {
    console.log('🔧 [ChatMessage] Tool Executions Debug:', {
      total: validToolExecutions.length,
      running: runningTools.length,
      completed: toolsWithResults.length,
      lastToolStartMessage: lastToolStartMessage?.tool_name,
      lastToolStartStatus: lastToolStartMessage?.status,
      firstFiveReversed: sortedToolResults.slice(0, 5).map(t => t.tool_name)
    });
  }

  const shouldShowExpandButton = hasToolResults;

  // Helper function to get icon for tool execution based on friendly_message type
  const getToolIcon = (type?: string): React.ReactNode => {
    if (!type) return <Icon name={IconName.Code} size='xs' variant="secondary" />;

    // Map tool types to icons based on backend tool_execution_service.py
    const iconMap: Record<string, IconName> = {
      // File Operations
      "Read": IconName.Read,
      "Update": IconName.Edit,
      "Create": IconName.Edit,
      "Delete": IconName.Delete,

      // Directory & Search Operations
      "List": IconName.ListFiles,
      "Search": IconName.Search,
      "ProjectTree": IconName.ProjectTree,
      "SearchReplace": IconName.Edit,
      "CreateDir": IconName.Folder,

      // Port Operations
      "Port": IconName.Port,

      // Credential Operations
      "RequestCredentials": IconName.Credentials,
      "WriteCredentials": IconName.Credentials,
      "GetCredentials": IconName.Credentials,

      // Web Tool Operations
      "WebtoolText": IconName.Web,
      "WebtoolHTML": IconName.Web,
      "WebtoolScreenshot": IconName.Web,
      "WebtoolDownload": IconName.Web,

      // Container & Command Operations
      "Container": IconName.Container,
      "Command": IconName.Run,

      // Fallback
      "Other": IconName.More,
    };

    return <Icon name={iconMap[type as keyof typeof iconMap] || IconName.Code} size='xs' className='text-text-secondary' inheritColor={true} />;
  };


  // Check if there are any attachments - use useMemo to prevent recalculation
  const hasAttachments = React.useMemo(() =>
    attachments && attachments.length > 0,
    [attachments]
  );

  // Check if this is a CAB-TO-DESIGN transition message
  const isCABToDesignMessage = content?.includes('```CAB-TO-DESIGN');

  if (type === 'user') {
    return (
      <div className={`flex flex-col gap-bg-v2-xs py-bg-v2-md ${className} ${isFullWidthMessages ? 'px-bg-v2-xl' : ''}`}>
        <div className="flex justify-end w-full pl-bg-v2-6xl">
          <div className="flex flex-col gap-bg-v2-md max-w-bg-v2-container bg-bg-brand-primary-alt px-bg-v2-lg py-bg-v2-md rounded-bg-v2-md">
            <div className="flex items-center gap-bg-v2-md">
              <Avatar
                user={user}
                size="sm"
                variant="brand"
                title={user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.username}
              />
              <div className="flex flex-col gap-bg-v2-xs">
                {isCABToDesignMessage ? (
                  <div className="flex flex-col">
                    <Text variant="text-sm" weight="semibold" color="brand-primary">
                      CAB to Prototype
                    </Text>
                    <Text variant="text-xs" color="secondary">
                      Transforming CAB data model into Low Code Platform prototype
                    </Text>
                  </div>
                ) : (
                  <Text variant="text-sm" weight="medium" color="primary" className="whitespace-pre-wrap">
                    {content}
                  </Text>
                )}
                {hasAttachments && (
                  <FileAttachmentsToolTip attachments={attachments} />
                )}
              </div>
            </div>
          </div>
        </div>
        {timestamp && (
          <div className="flex justify-end w-full">
            <div className="flex items-center gap-bg-v2-xs">
              <Text variant="text-xs" weight="medium" color="quaternary">
                {timestamp}
              </Text>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Assistant message
  return (
    <>
      <div className={`bg-bg-primary rounded-bg-v2-md gap-bg-v2-lg ${className} ${isFullWidthMessages ? 'px-bg-v2-xl' : ''}`}>
        <div className={`flex flex-col gap-bg-v2-md w-1/2 border border-border-secondary rounded-bg-v2-md p-bg-v2-lg ${isFullWidthMessages && isDesignMode ? 'w-full' :''}`}>
          <div className='flex gap-bg-v2-xs justify-between'>
            <div className='flex items-center gap-bg-v2-md'>
              <div className='flex items-center justify-center w-[29px] h-[29px] rounded-full border border-border-secondary bg-bg-primary shrink-0'>
                <Icon name={IconName.Logo} size='md' />
              </div>
              <div className='flex flex-col gap-bg-v2-xxs'>
                <Text variant="text-sm" weight="bold" color="primary" as={"span"} className="font-bg-v2-sans">
                  BlueGenAI Agent
                </Text>
                {isElaboration && (
                  <Text variant="text-xs" weight="medium" color="brand-primary" as={"span"}>
                    Detailed Requirements Analysis
                  </Text>
                )}
              </div>
            </div>
            <div className='flex items-center gap-bg-v2-xs'>
              {isElaboration && iterationCount >= 1 && (
                <div className='px-bg-v2-md py-bg-v2-xs bg-utility-blue-50 rounded-bg-v2-full'>
                  <Text variant="text-xs" weight="medium" color="brand-primary" as={"span"}>
                    Iteration {iterationCount}
                  </Text>
                </div>
              )}
              {isElaboration && isUpdated && iterationCount <= 1 && (
                <div className='flex items-center gap-bg-v2-xs px-bg-v2-md py-bg-v2-xs bg-utility-success-50 rounded-bg-v2-full'>
                  <Icon name={IconName.Edit} size='xs' variant="secondary" />
                  <Text variant="text-xs" weight="medium" color="success" as={"span"}>
                    Updated{updatedAt ? ` ${updatedAt.toLocaleTimeString()}` : ''}
                  </Text>
                </div>
              )}
              {!isThinking && !isElaboration && (
                <button
                  ref={statsButtonRef}
                  onClick={() => setIsTooltipOpen(!isTooltipOpen)}
                  className="inline-flex items-center justify-center w-[24px] h-[24px] rounded-bg-v2-full bg-bg-brand-primary-alt hover:bg-bg-brand-primary-hover transition-colors"
                  title="View Statistics"
                >
                  <Icon name={IconName.InfoCircle} size='xs' variant="brand-icon" />
                </button>
              )}


              {shouldShowExpandButton && (
                <div
                  className='flex items-center justify-center w-[24px] h-[24px] border border-border-secondary rounded-bg-v2-full hover:bg-bg-secondary-hover cursor-pointer'
                  onClick={() => setIsExpanded(!isExpanded)}
                >
                  <Icon
                    name={IconName.ChevronRight}
                    className={`transition-transform duration-200 ${isExpanded ? '-rotate-90' : 'rotate-90'}`}
                    size='sm'
                    variant={"secondary"}
                  />
                </div>
              )}
            </div>
          </div>

          {/* THINKING STATE - Tool Execution Queue */}
          {isThinking && (
            <div className='flex flex-col gap-bg-v2-xs' style={{ paddingLeft: 'calc(29px + var(--spacing-md))' }}>
              {/* Thinking animation with message */}
              <div className='flex gap-bg-v2-xs'>
                <LoadingDots />
                <Text variant="text-xs" weight="medium" color="quaternary" as={"span"}>
                  {thinkingMessage}
                </Text>
              </div>

              {/* Latest tool_start - persists until thinking completes or new tool starts */}
              {lastToolStartMessage && (
                <div className='flex items-center gap-bg-v2-xs min-w-0'>
                  <span className="flex-shrink-0">
                    {getToolIcon(lastToolStartMessage.friendly_message?.type)}
                  </span>
                  <Text variant="text-xs" weight="medium" color="primary" as={"span"} className="capitalize flex-shrink-0">
                    {lastToolStartMessage.friendly_message?.message}
                  </Text>
                  {(lastToolStartMessage.friendly_message?.file && lastToolStartMessage.friendly_message.file !== '.') &&
                    (
                      <span
                        className="truncate min-w-0"
                        title={lastToolStartMessage.friendly_message?.file}
                        onClick={() => {
                          const file = lastToolStartMessage.friendly_message?.file;
                          if (file && onFileClick) {
                            onFileClick(file);
                          }
                        }}
                      >
                        <Text variant="text-xs" weight="medium" color="brand-secondary" as={"span"} className="cursor-pointer hover:underline">
                          {lastToolStartMessage.friendly_message?.file}
                          <Icon name={IconName.ArrowNarrowUpRight} size='xs' variant="brand-secondary" className="flex-shrink-0" />
                        </Text>
                      </span>
                    )}
                </div>
              )}

              {/* All tool results - shown in expanded view only, latest first */}
              {isExpanded && sortedToolResults.length > 0 && (
                <>
                  {sortedToolResults.map((tool) => (
                    <div key={`${tool.id}-${tool.timestamp_complete || tool.timestamp_start}`} className='flex items-center gap-bg-v2-xs min-w-0'>
                      <span className="flex-shrink-0">
                        {getToolIcon(tool.friendly_message?.type)}
                      </span>
                      <Text variant="text-xs" weight="medium" color="primary" as={"span"} className="capitalize flex-shrink-0">
                        {tool.friendly_message?.message}
                      </Text>
                      {(tool.friendly_message?.file && tool.friendly_message.file !== '.') &&
                        (
                          <span
                            className="truncate min-w-0"
                            title={tool.friendly_message?.file}
                            onClick={() => {
                              const file = tool.friendly_message?.file;
                              if (file && onFileClick) {
                                onFileClick(file);
                              }
                            }}
                          >
                            <Text variant="text-xs" weight="medium" color="brand-secondary" as={"span"} className="cursor-pointer hover:underline">
                              {tool.friendly_message?.file}
                              <Icon name={IconName.ArrowNarrowUpRight} size='xs' variant="brand-secondary" className="flex-shrink-0" />
                            </Text>
                          </span>
                        )}
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {/* COMPLETED STATE - Tool Executions and Thought */}
          {
            shouldShowThoughtDuration && (
              <div className='flex flex-col gap-bg-v2-xs' style={{ paddingLeft: 'calc(29px + var(--spacing-md))' }}>
                <div className='flex gap-bg-v2-xs'>
                  <Icon name={IconName.Thought} size='sm' className='text-text-quaternary' inheritColor />
                  <Text variant="text-xs" weight="medium" color="quaternary" as={"span"}>
                    Thought For {
                      developmentTiming?.duration_seconds
                        ? formatRoundedDuration(developmentTiming.duration_seconds)
                        : brainstormTiming?.duration_seconds
                          ? formatRoundedDuration(brainstormTiming.duration_seconds)
                          : developmentTiming?.duration_formatted || brainstormTiming?.duration_formatted
                    }
                  </Text>
                </div>
                {
                  isExpanded && hasToolResults && (
                    <>
                      {sortedToolResults.map((tool) => (
                        <div key={`${tool.id}-${tool.timestamp_complete || tool.timestamp_start}`} className='flex items-center gap-bg-v2-xs min-w-0'>
                          <span className="flex-shrink-0">
                            {getToolIcon(tool.friendly_message?.type)}
                          </span>
                          <Text variant="text-xs" weight="medium" color="primary" as={"span"} className="capitalize flex-shrink-0">
                            {tool.friendly_message?.message}
                          </Text>
                          {(tool.friendly_message?.file && tool.friendly_message.file !== '.') &&
                            (
                              <span
                                className="truncate min-w-0"
                                title={tool.friendly_message?.file}
                                onClick={() => {
                                  const file = tool.friendly_message?.file;
                                  if (file && onFileClick) {
                                    onFileClick(file);
                                  }
                                }}
                              >
                                <Text variant="text-xs" weight="medium" color="brand-secondary" as={"span"} className="cursor-pointer hover:underline">
                                  {tool.friendly_message?.file}
                                  <Icon name={IconName.ArrowNarrowUpRight} size='xs' variant="brand-secondary" className="flex-shrink-0" />
                                </Text>
                              </span>
                            )}
                        </div>
                      ))}
                    </>
                  )
                }
              </div>
            )
          }
        </div>

        {/* Content */}
        {content && (
          <div
            ref={contentRef}
        className={`px-bg-v2-xs py-bg-v2-md transition-all duration-500 ease-in-out ${
          isElaboration && isAnimating 
                ? (showNewContent
                  ? 'opacity-100 transform translate-y-0'
                  : 'opacity-30 transform translate-y-1')
                : 'opacity-100 transform translate-y-0'
              }`}
          >
            {/* Animation overlay during content transition */}
            {isElaboration && isAnimating && !showNewContent && (
              <div className="absolute inset-0 bg-gradient-to-r from-bg-blue-50 via-utility-blue-200 to-bg-blue-50 rounded-bg-v2-md opacity-80 pointer-events-none z-10 flex items-center justify-center">
                <div className="flex items-center gap-bg-v2-xs">
                  <Icon name={IconName.Loading} size='sm' spin />
                  <Text variant="text-sm" weight="medium" color="brand-primary" as={"span"}>
                    Updating requirements...
                  </Text>
                </div>
              </div>
            )}

            {/* Parse and render content with artifacts */}
            {(() => {
              const { parts } = parseArtifacts(content, false);

              // Separate text content and artifacts
              const textParts: string[] = [];
              const artifactParts: any[] = [];

              parts.forEach(part => {
                if (typeof part === 'string') {
                  textParts.push(part);
                } else {
                  artifactParts.push(part);
                }
              });

              return (
                <div className="flex flex-col gap-bg-v2-lg">
                  {/* Render all text content first */}
                  {textParts.length > 0 && (
                    <div className="flex flex-col gap-bg-v2-xs">
                      {textParts.map((part, index) => (
                        <ReactMarkdown
                          className="prose prose-sm max-w-none text-text-secondary [&_*]:text-text-secondary [&_pre]:bg-bg-secondary [&_code]:bg-bg-secondary [&_pre]:text-text-primary [&_code]:text-text-primary"
                          key={index}
                          remarkPlugins={[remarkGfm]}
                          components={{
                            code: ({ node, inline, className, children, ...props }: any) => {
                              return <code className={className} {...props}>{children}</code>;
                            },
                            a: ({ node, children, href, ...props }: any) => {
                              return (
                                <a
                                  href={href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  {...props}
                                >
                                  {children}
                                </a>
                              );
                            }
                          }}
                        >
                          {part}
                        </ReactMarkdown>
                      ))}
                    </div>
                  )}

                  {/* Render all artifact cards at the end */}
                  {artifactParts.length > 0 && (
                    <div className="flex flex-col gap-bg-v2-md">
                      {artifactParts.map((part, index) => {
                        return (
                          <ArtifactCard
                            key={index}
                            project={project}
                            title={part.title}
                            type={part.type || 'text/html'}
                            status="Preview"
                            className={`${(isArtifactDetailOpen || isFullWidthMessages) ? 'w-full' : 'w-1/2'}`}
                            onClick={() => {
                              if (onArtifactClick) {
                                // Attach messageId to artifact before passing to handler
                                const artifactWithMessageId = { ...part, messageId: messageId };
                                onArtifactClick(artifactWithMessageId);
                              }
                            }}
                            artifactId={undefined}
                            identifier={part.identifier}
                            messageId={messageId}
                            content={part.content}
                            version={part.version}
                            threadId={threadId}
                            isSaved={false}
                            isStreaming={isStreaming}
                            onArtifactSaved={() => {
                              // Trigger global event for requirement folders to reload
                              window.dispatchEvent(new Event('artifactSaved'));
                            }}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* Refinement Input Section */}
        {isElaboration && showRefinementInput && (
          <div className="bg-bg-brand-primary-alt border border-border-secondary rounded-bg-v2-md p-bg-v2-lg gap-bg-v2-xl flex flex-col">
            <div className='flex items-center gap-bg-v2-sm'>
              <Icon name={IconName.Edit} size='sm' variant="secondary" />
              <Text variant="text-sm" weight="semibold" color="primary" as={"span"}>
                Suggest Changes or Additions:
              </Text>
            </div>
            <textarea
              value={refinementText}
              onChange={(e) => setRefinementText(e.target.value)}
              placeholder="What would you like to change, add, or clarify about these requirements?"
              className="w-full placeholder:text-text-placeholder p-bg-v2-md border border-border-secondary rounded-bg-v2-md outline-none focus:ring-focus-ring focus:border-border-brand resize-none bg-bg-primary text-text-primary font-bg-v2-sans text-bg-v2-sm"
              rows={4}
              disabled={isRefining}
            />
            <div className="flex gap-bg-v2-md">
              <Button
                label={isRefining ? 'Updating...' : 'Update Requirements'}
                onClick={handleRefinementSubmit}
                disabled={isRefining || !refinementText.trim()}
                loading={isRefining}
                variant="primary"
                size="small"
              />
              <Button
                label="Cancel"
                onClick={cancelRefinement}
                disabled={isRefining}
                variant="outline"
                size="small"
              />
            </div>
          </div>
        )}

        {/* Action Buttons - Only show for active elaboration and users with update permission */}
        {!showRefinementInput && isElaboration && showActionButtons && hasUpdatePermission && (
          <div className="bg-bg-brand-primary-alt border border-border-secondary rounded-bg-v2-md p-bg-v2-lg flex flex-col gap-bg-v2-md">
            <Text variant="text-sm" weight="semibold" color="primary" as={"span"}>
              Elaboration Actions
            </Text>
            <div className="flex items-center justify-end gap-bg-v2-md">
              <Button
                label={isCancelling ? 'Cancelling...' : 'Cancel'}
                onClick={handleCancel}
                disabled={isCancelling || isApproving || isRefining || showRefinementInput}
                loading={isCancelling}
                variant="outline"
                size="small"
              />

              <Button
                label="Suggest Changes"
                onClick={handleRefineClick}
                disabled={isApproving || isRefining || isCancelling || showRefinementInput}
                variant="primary"
                size="small"
              />
              <Button
                label={isApproving ? 'Starting Development...' : (
                  <div className="flex items-center gap-bg-v2-md">
                    <Icon name={IconName.Check} size='xs' inheritColor={true} />
                    <span>Approve & Start Development</span>
                  </div>
                )}
                onClick={handleApprove}
                disabled={isApproving || isRefining || isCancelling || showRefinementInput}
                loading={isApproving}
                className={"bg-fg-success-primary text-text-white hover:bg-fg-success-secondary"}
                variant={"secondary"}
                size="small"
              />
            </div>
          </div>
        )}

        {/* Processing indicator */}
        {isElaboration && isProcessing && (
          <div className="flex items-center justify-center py-bg-v2-lg gap-bg-v2-xs">
            <Icon name={IconName.Loading} size='md' spin />
            <Text variant="text-sm" weight="medium" color="brand-primary" as={"span"}>
              Processing your request...
            </Text>
          </div>
        )}
      </div>

      {/* Token Usage Tooltip */}
      <TokenUsageTooltip
        isOpen={isTooltipOpen}
        onClose={() => setIsTooltipOpen(false)}
        tokenUsage={tokenUsage as ITokenUsage | undefined}
        developmentTiming={developmentTiming as IDevelopmentTiming | undefined}
        brainstormTiming={brainstormTiming as IDevelopmentTiming | undefined}
        triggerRef={statsButtonRef}
      />
    </>
  );
};